// apps/web/src/components/ai/GlobalAiAssistantRoot.jsx
// Globaler KI-Einstieg: Provider, FAB, Slide-in-Panel, Tastenkürzel ⌘K / Strg+K.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AIProvider, AIFAB } from "./ai-context.jsx";
import { AIChatPanel } from "./ai-chat-panel.jsx";

/**
 * @param {{
 *   t: (key: string, params?: Record<string, string | number>) => string;
 *   authenticated?: boolean;
 *   userDisplayName?: string;
 *   disabled?: boolean;
 *   pageMode?: boolean;
 *   pageChatMountSelector?: string;
 *   children?: import("react").ReactNode;
 * }} props
 */
export function GlobalAiAssistantRoot({
  t,
  authenticated = false,
  userDisplayName = "",
  disabled = false,
  pageMode = false,
  pageChatMountSelector = "",
  children = null,
}) {
  useEffect(() => {
    if (disabled) return undefined;
    const onKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod || event.key.toLowerCase() !== "k") return;
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        event.target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("tm:ai-toggle-request"));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [disabled]);

  if (disabled) return children;

  return (
    <AIProvider t={t} userDisplayName={userDisplayName} pageMode={pageMode}>
      {children}
      {authenticated ? (
        <GlobalAiAssistantChrome
          pageMode={pageMode}
          pageChatMountSelector={pageChatMountSelector}
        />
      ) : null}
    </AIProvider>
  );
}

function GlobalAiAssistantChrome({ pageMode, pageChatMountSelector }) {
  const [pageMount, setPageMount] = useState(null);

  useEffect(() => {
    if (!pageMode || !pageChatMountSelector) {
      setPageMount(null);
      return undefined;
    }
    const pick = () => document.querySelector(pageChatMountSelector);
    setPageMount(pick());
    const timer = window.setTimeout(() => setPageMount(pick()), 0);
    return () => window.clearTimeout(timer);
  }, [pageMode, pageChatMountSelector]);

  const panel = <AIChatPanel layout={pageMode ? "page" : "drawer"} />;

  return (
    <>
      {!pageMode ? <AIFAB /> : null}
      {pageMode && pageMount ? createPortal(panel, pageMount) : null}
      {!pageMode ? panel : null}
    </>
  );
}
